// Template-specific vCard layouts
// These are full-page components that render the vCard data

import { 
  Phone, 
  MessageCircle, 
  Mail, 
  Share2, 
  Download,
  Linkedin,
  Instagram,
  Github,
  Facebook,
  Youtube,
  Twitter,
  Globe,
  MapPin
} from 'lucide-react'

// Template 1: Simple Identity - Clean, minimal design with dark mode
export function SimpleIdentityTemplate({ vcard, onSaveContact, onShare }) {
  return (
    <div className="simple-identity-template">
      <div className="si-container">
        <main className="si-main">
          {/* Profile Header */}
          <div className="si-profile">
            <div 
              className="si-avatar"
              style={{ 
                backgroundImage: vcard.profileImage ? `url(${vcard.profileImage})` : 'none'
              }}
            >
              {!vcard.profileImage && (
                <span className="si-avatar-letter">
                  {vcard.name?.[0] || 'U'}
                </span>
              )}
            </div>
            <div className="si-info">
              <h1 className="si-name">{vcard.name || 'Your Name'}</h1>
              <p className="si-title">{vcard.title || 'Your Title'}</p>
              {vcard.company && (
                <p className="si-company">{vcard.company}</p>
              )}
            </div>
          </div>

          {/* Primary Actions */}
          <div className="si-actions-primary">
            {vcard.phone && (
              <a href={`tel:${vcard.phone}`} className="si-btn-primary">
                <Phone size={20} />
                <span>Call</span>
              </a>
            )}
            {vcard.whatsapp && (
              <a 
                href={`https://wa.me/${vcard.whatsapp.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="si-btn-primary"
              >
                <MessageCircle size={20} />
                <span>WhatsApp</span>
              </a>
            )}
            {vcard.email && (
              <a href={`mailto:${vcard.email}`} className="si-btn-primary">
                <Mail size={20} />
                <span>Email</span>
              </a>
            )}
          </div>

          {/* Secondary Actions */}
          <div className="si-actions-secondary">
            <button onClick={onSaveContact} className="si-btn-secondary">
              <Download size={20} />
              <span>Save Contact</span>
            </button>
            <button onClick={onShare} className="si-btn-secondary">
              <Share2 size={20} />
              <span>Share vCard</span>
            </button>
          </div>

          {/* About Section */}
          {vcard.about && (
            <div className="si-section">
              <h3 className="si-section-title">About</h3>
              <p className="si-about">{vcard.about}</p>
            </div>
          )}

          {/* Services */}
          {vcard.services && vcard.services.length > 0 && (
            <div className="si-section">
              <h3 className="si-section-title">Services</h3>
              <div className="si-services">
                {vcard.services.map((service, i) => (
                  <span key={i} className="si-service-chip">{service}</span>
                ))}
              </div>
            </div>
          )}

          {/* Social Links */}
          {vcard.social && Object.values(vcard.social).some(v => v) && (
            <div className="si-social-grid">
              {vcard.social.linkedin && (
                <a 
                  href={vcard.social.linkedin.startsWith('http') ? vcard.social.linkedin : `https://linkedin.com/in/${vcard.social.linkedin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="si-social-item"
                >
                  <div className="si-social-icon">
                    <Linkedin size={24} />
                  </div>
                  <span>LinkedIn</span>
                </a>
              )}
              {vcard.social.github && (
                <a 
                  href={vcard.social.github.startsWith('http') ? vcard.social.github : `https://github.com/${vcard.social.github}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="si-social-item"
                >
                  <div className="si-social-icon">
                    <Github size={24} />
                  </div>
                  <span>GitHub</span>
                </a>
              )}
              {vcard.social.instagram && (
                <a 
                  href={vcard.social.instagram.startsWith('http') ? vcard.social.instagram : `https://instagram.com/${vcard.social.instagram.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="si-social-item"
                >
                  <div className="si-social-icon">
                    <Instagram size={24} />
                  </div>
                  <span>Instagram</span>
                </a>
              )}
              {vcard.social.facebook && (
                <a 
                  href={vcard.social.facebook.startsWith('http') ? vcard.social.facebook : `https://facebook.com/${vcard.social.facebook}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="si-social-item"
                >
                  <div className="si-social-icon">
                    <Facebook size={24} />
                  </div>
                  <span>Facebook</span>
                </a>
              )}
              {vcard.social.youtube && (
                <a 
                  href={vcard.social.youtube.startsWith('http') ? vcard.social.youtube : `https://youtube.com/${vcard.social.youtube}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="si-social-item"
                >
                  <div className="si-social-icon">
                    <Youtube size={24} />
                  </div>
                  <span>YouTube</span>
                </a>
              )}
              {vcard.social.twitter && (
                <a 
                  href={vcard.social.twitter.startsWith('http') ? vcard.social.twitter : `https://twitter.com/${vcard.social.twitter.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="si-social-item"
                >
                  <div className="si-social-icon">
                    <Twitter size={24} />
                  </div>
                  <span>Twitter</span>
                </a>
              )}
              {vcard.social.website && (
                <a 
                  href={vcard.social.website.startsWith('http') ? vcard.social.website : `https://${vcard.social.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="si-social-item"
                >
                  <div className="si-social-icon">
                    <Globe size={24} />
                  </div>
                  <span>Website</span>
                </a>
              )}
            </div>
          )}

          {/* Location */}
          {vcard.address && (
            <div className="si-section">
              <a 
                href={`https://maps.google.com/?q=${encodeURIComponent(vcard.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="si-location"
              >
                <MapPin size={20} />
                <span>{vcard.address}</span>
              </a>
            </div>
          )}

          {/* Footer */}
          <footer className="si-footer">
            <p>Powered by <a href="/">Digital vCard</a></p>
          </footer>
        </main>
      </div>
    </div>
  )
}

export default { SimpleIdentityTemplate }
